import { useState } from 'react';
import {
  Box,
  Button,
  Typography,
  CircularProgress,
  IconButton,
  Card,
  CardMedia,
  Alert,
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DeleteIcon from '@mui/icons-material/Delete';
import { getMediaType, type MediaType } from '../../types/configuration';

interface SplashScreenInputProps {
  value?: string;
  onChange: (url: string | null) => void;
  onUpload: (file: File) => Promise<string>;
  onRemove?: () => Promise<void>;
  disabled?: boolean;
}

export const SplashScreenInput = ({
  value,
  onChange,
  onUpload,
  onRemove,
  disabled = false,
}: SplashScreenInputProps) => {
  const [uploading, setUploading] = useState(false);
  const [removing, setRemoving] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);

  const mediaType: MediaType | null = value ? getMediaType(value) : null;

  const validateFile = (file: File): string | null => {
    // Check if it's image or video
    const isImage = file.type.startsWith('image/');
    const isVideo = file.type.startsWith('video/');

    if (!isImage && !isVideo) {
      return 'Please select an image or video file';
    }

    // Validate file size
    const maxSize = isImage ? 5 * 1024 * 1024 : 20 * 1024 * 1024; // 5MB for images, 20MB for videos
    if (file.size > maxSize) {
      const limit = isImage ? '5MB' : '20MB';
      return `File size must be less than ${limit}`;
    }

    // Validate file format
    const allowedImageTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/webp', 'image/gif'];
    const allowedVideoTypes = ['video/mp4', 'video/webm'];

    if (isImage && !allowedImageTypes.includes(file.type)) {
      return 'Unsupported image format. Please use PNG, JPEG, WebP, or GIF';
    }

    if (isVideo && !allowedVideoTypes.includes(file.type)) {
      return 'Unsupported video format. Please use MP4 or WebM';
    }

    return null;
  };

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file
    const validationError = validateFile(file);
    if (validationError) {
      setUploadError(validationError);
      return;
    }

    setUploadError(null);
    setUploading(true);

    try {
      const url = await onUpload(file);
      onChange(url);
    } catch (error) {
      setUploadError('Failed to upload file. Please try again.');
    } finally {
      setUploading(false);
      // Reset file input
      event.target.value = '';
    }
  };

  const handleRemove = async () => {
    if (!onRemove) {
      onChange(null);
      return;
    }

    setRemoving(true);
    try {
      await onRemove();
      onChange(null);
    } catch (error) {
      setUploadError('Failed to remove splash screen. Please try again.');
    } finally {
      setRemoving(false);
    }
  };

  return (
    <Box sx={{ mt: 3 }}>
      <Typography variant="subtitle1" gutterBottom>
        Splash Screen (Optional)
      </Typography>
      <Typography variant="body2" color="text.secondary" gutterBottom>
        Upload an image (PNG, JPEG, WebP, GIF - max 5MB) or video (MP4, WebM - max 20MB)
      </Typography>

      {uploadError && (
        <Alert severity="error" sx={{ mt: 2, mb: 2 }} onClose={() => setUploadError(null)}>
          {uploadError}
        </Alert>
      )}

      {value && (
        <Card sx={{ mt: 2, mb: 2, maxWidth: 400 }}>
          {mediaType === 'image' ? (
            <CardMedia
              component="img"
              image={value}
              alt="Splash screen preview"
              sx={{ height: 300, objectFit: 'contain', bgcolor: 'grey.100' }}
            />
          ) : (
            <CardMedia
              component="video"
              src={value}
              controls
              sx={{ height: 300, bgcolor: 'grey.100' }}
            />
          )}
          <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="caption" color="text.secondary" sx={{ wordBreak: 'break-all' }}>
              {value}
            </Typography>
            <IconButton
              color="error"
              onClick={handleRemove}
              disabled={disabled || removing}
              size="small"
            >
              {removing ? <CircularProgress size={20} /> : <DeleteIcon />}
            </IconButton>
          </Box>
        </Card>
      )}

      <Box>
        <input
          accept="image/png,image/jpeg,image/jpg,image/webp,image/gif,video/mp4,video/webm"
          style={{ display: 'none' }}
          id="splash-screen-upload"
          type="file"
          onChange={handleFileChange}
          disabled={disabled || uploading}
        />
        <label htmlFor="splash-screen-upload">
          <Button
            variant="outlined"
            component="span"
            startIcon={uploading ? <CircularProgress size={20} /> : <CloudUploadIcon />}
            disabled={disabled || uploading}
          >
            {uploading ? 'Uploading...' : value ? 'Replace Splash Screen' : 'Upload Splash Screen'}
          </Button>
        </label>
      </Box>
    </Box>
  );
};
